import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, User, Users, BarChart3, Brain, Layers, Sparkles, TrendingUp, MessageSquare, Lock, UserRound, Type, SlidersHorizontal } from 'lucide-react';

/* ─── About Me — gradient sliders from How It Works ─── */
function AboutMeSlide() {
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>About Me Questions</h3>
        <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100">Always On</span>
      </div>
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-lg bg-sky-50 flex items-center justify-center"><TrendingUp className="w-3.5 h-3.5 text-sky-500" /></div>
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Overall Sentiment</span>
        </div>
        <p className="text-sm text-slate-600 mb-3">How do you feel this project is going?</p>
        <div className="relative h-3 rounded-full" style={{ background: 'linear-gradient(to right, #f9a8a8, #fcd77e, #a7e8bd)' }}>
          <div className="absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 border-white bg-white shadow-md" style={{ left: '60%' }} />
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-[9px] text-rose-300">Worst Project Ever</span>
          <span className="text-[10px] font-bold text-[#5AA2AE]">62%</span>
          <span className="text-[9px] text-emerald-400">Best Project Ever</span>
        </div>
      </div>
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center"><MessageSquare className="w-3.5 h-3.5 text-amber-500" /></div>
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Unspoken Problem</span>
        </div>
        <p className="text-sm text-slate-600 mb-2">Is there a problem or issue that we are not discussing openly?</p>
        <div className="bg-white rounded-lg border border-slate-200 px-3 py-2">
          <p className="text-sm text-slate-500 italic">Contoso can really throw a wrench in o<span className="inline-block w-0.5 h-4 bg-[#5AA2AE] animate-pulse align-middle" /></p>
        </div>
      </div>
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center"><Lock className="w-3.5 h-3.5 text-purple-500" /></div>
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Safe to Speak</span>
        </div>
        <p className="text-sm text-slate-600 mb-3">Do you feel safe to speak up and share an unpopular opinion?</p>
        <div className="relative h-3 rounded-full" style={{ background: 'linear-gradient(to right, #f9a8a8, #fcd77e, #a7e8bd)' }}>
          <div className="absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 border-white bg-white shadow-md" style={{ left: '53%' }} />
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-[9px] text-rose-300">Not Safe At All</span>
          <span className="text-[10px] font-bold text-[#5AA2AE]">55%</span>
          <span className="text-[9px] text-emerald-400">Completely Safe</span>
        </div>
      </div>
      <div className="flex items-center justify-center gap-1.5 pt-1">
        <Lock className="w-3 h-3 text-emerald-400" />
        <span className="text-[10px] text-slate-400">All responses are anonymized</span>
      </div>
    </div>
  );
}

/* ─── About Others — dark galaxy with expanded individuals per segment ─── */
function AboutOthersSlide() {
  const lc = '#41BDD1';
  // Layout: all coordinates in viewBox units (0-100)
  const cx = 50, cy = 45; // Center "You"
  const groups = [
    { name: 'Engineering', color: '#3B82F6', x: 25, y: 22, people: [
      { x: 10, y: 8 }, { x: 38, y: 8 }, { x: 10, y: 36, selected: true }, { x: 25, y: 40 },
    ]},
    { name: 'Sales', color: '#10B981', x: 75, y: 20, people: [
      { x: 65, y: 6 }, { x: 88, y: 6 }, { x: 62, y: 32 }, { x: 90, y: 30 },
    ]},
    { name: 'Leadership', color: '#A855F7', x: 22, y: 68, people: [
      { x: 8, y: 58 }, { x: 12, y: 82 }, { x: 34, y: 80 },
    ]},
    { name: 'Partners', color: '#F59E0B', x: 76, y: 66, people: [
      { x: 65, y: 52 }, { x: 88, y: 52 }, { x: 62, y: 76 }, { x: 90, y: 76 }, { x: 76, y: 84 }, { x: 88, y: 66 },
    ]},
  ];
  return (
    <div className="bg-[#1a1f2e] rounded-b-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Users className="h-4 w-4 text-[#7FCDC1]" />
        <span className="text-sm font-semibold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>About Others</span>
        <span className="text-[10px] text-slate-500 ml-auto">Your stakeholder network</span>
      </div>
      <div className="relative h-[260px]">
        {/* SVG connections — rendered first, behind all node divs */}
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
          {/* Lines from center "You" to each segment */}
          <line x1={cx} y1={cy} x2={25} y2={22} stroke={lc} strokeWidth="0.6" />
          <line x1={cx} y1={cy} x2={75} y2={20} stroke={lc} strokeWidth="0.6" />
          <line x1={cx} y1={cy} x2={22} y2={68} stroke={lc} strokeWidth="0.6" />
          <line x1={cx} y1={cy} x2={76} y2={66} stroke={lc} strokeWidth="0.6" />
          {/* Engineering → people */}
          <line x1={25} y1={22} x2={10} y2={8} stroke={lc} strokeWidth="0.5" />
          <line x1={25} y1={22} x2={38} y2={8} stroke={lc} strokeWidth="0.5" />
          <line x1={25} y1={22} x2={10} y2={36} stroke={lc} strokeWidth="0.5" />
          <line x1={25} y1={22} x2={25} y2={40} stroke={lc} strokeWidth="0.5" />
          {/* Sales → people */}
          <line x1={75} y1={20} x2={65} y2={6} stroke={lc} strokeWidth="0.5" />
          <line x1={75} y1={20} x2={88} y2={6} stroke={lc} strokeWidth="0.5" />
          <line x1={75} y1={20} x2={62} y2={32} stroke={lc} strokeWidth="0.5" />
          <line x1={75} y1={20} x2={90} y2={30} stroke={lc} strokeWidth="0.5" />
          {/* Leadership → people */}
          <line x1={22} y1={68} x2={8} y2={58} stroke={lc} strokeWidth="0.5" />
          <line x1={22} y1={68} x2={12} y2={82} stroke={lc} strokeWidth="0.5" />
          <line x1={22} y1={68} x2={34} y2={80} stroke={lc} strokeWidth="0.5" />
          {/* Partners → people */}
          <line x1={76} y1={66} x2={65} y2={52} stroke={lc} strokeWidth="0.5" />
          <line x1={76} y1={66} x2={88} y2={52} stroke={lc} strokeWidth="0.5" />
          <line x1={76} y1={66} x2={62} y2={76} stroke={lc} strokeWidth="0.5" />
          <line x1={76} y1={66} x2={90} y2={76} stroke={lc} strokeWidth="0.5" />
          <line x1={76} y1={66} x2={76} y2={84} stroke={lc} strokeWidth="0.5" />
          <line x1={76} y1={66} x2={88} y2={66} stroke={lc} strokeWidth="0.5" />
          {/* Cross-segment dashed connections from selected stakeholder (10,36) */}
          <line x1={10} y1={36} x2={62} y2={32} stroke={lc} strokeWidth="0.4" strokeDasharray="1.5 0.8" />
          <line x1={10} y1={36} x2={12} y2={82} stroke={lc} strokeWidth="0.4" strokeDasharray="1.5 0.8" />
        </svg>
        {/* Center "You" */}
        <div className="absolute left-1/2 top-[45%] -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#5AA2AE] flex items-center justify-center shadow-lg shadow-[#5AA2AE]/30 z-10">
          <span className="text-white text-[10px] font-bold">You</span>
        </div>
        {/* Segment nodes */}
        {groups.map((g, i) => (
          <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2 z-[5]" style={{ left: `${g.x}%`, top: `${g.y}%` }}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: g.color, boxShadow: `0 0 16px ${g.color}40` }}>
              <span className="text-white text-[10px] font-bold">{g.people.length}</span>
            </div>
            <span className="text-[8px] text-slate-400 block text-center mt-0.5">{g.name}</span>
          </div>
        ))}
        {/* Individual people nodes */}
        {groups.map((g, gi) => g.people.map((p, pi) => (
          <div key={`p-${gi}-${pi}`} className="absolute -translate-x-1/2 -translate-y-1/2 z-[3]" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${p.selected ? 'ring-2 ring-emerald-400 ring-offset-1 ring-offset-[#1a1f2e]' : ''}`} style={{ background: '#1a1f2e', border: `1.5px solid ${g.color}` }}>
              <UserRound className="w-2.5 h-2.5" style={{ color: g.color }} />
            </div>
          </div>
        )))}
      </div>
      {/* Legend */}
      <div className="flex items-center justify-center gap-3 mt-2">
        {groups.map((g, i) => (
          <div key={i} className="flex items-center gap-1"><div className="w-2 h-2 rounded-full" style={{ background: g.color }} /><span className="text-[9px] text-slate-400">{g.name}</span></div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-1 mt-1.5">
        <div className="w-3 h-3 rounded-full ring-2 ring-emerald-400 ring-offset-1 ring-offset-[#1a1f2e]" />
        <span className="text-[9px] text-emerald-400 ml-0.5">Selected stakeholder — cross-segment connections shown</span>
      </div>
    </div>
  );
}

/* ─── People Galaxy — Project center, segments, individuals, cross-connections ─── */
function PeopleGalaxySlide() {
  const segments = [
    { name: 'Contoso', x: 18, y: 24, color: '#3B82F6', people: [
      { x: 6, y: 12 }, { x: 26, y: 10 }, { x: 8, y: 38 },
    ]},
    { name: 'Acme', x: 82, y: 22, color: '#10B981', people: [
      { x: 72, y: 8 }, { x: 94, y: 10 }, { x: 92, y: 34, selected: true }, { x: 74, y: 36 },
    ]},
    { name: 'Vortex', x: 16, y: 70, color: '#F59E0B', people: [
      { x: 6, y: 82 }, { x: 26, y: 84 },
    ]},
    { name: 'Lunetra', x: 84, y: 72, color: '#A855F7', people: [
      { x: 72, y: 84 }, { x: 94, y: 82 }, { x: 92, y: 62 },
    ]},
  ];
  return (
    <div className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-[#5AA2AE]" />
        <span className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>People Galaxy</span>
        <span className="text-xs text-slate-400 ml-auto">314 stakeholders</span>
      </div>
      <div className="relative h-[280px] bg-gradient-to-b from-slate-50 to-slate-100 rounded-xl overflow-hidden">
        <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
          {/* Project → Segments */}
          {segments.map((s, i) => (
            <line key={`ps-${i}`} x1="50%" y1="48%" x2={`${s.x}%`} y2={`${s.y}%`} stroke="#94a3b8" strokeWidth="1.2" strokeDasharray="5 3" opacity="0.5" />
          ))}
          {/* Segments → People */}
          {segments.map((s, si) => s.people.map((p, pi) => (
            <line key={`sp-${si}-${pi}`} x1={`${s.x}%`} y1={`${s.y}%`} x2={`${p.x}%`} y2={`${p.y}%`} stroke={s.color} strokeWidth="0.8" opacity="0.35" />
          )))}
          {/* Cross-segment connections from selected individual (Acme person at 92,34) */}
          <line x1="92%" y1="34%" x2="8%" y2="38%" stroke="#ef4444" strokeWidth="1.2" opacity="0.5" strokeDasharray="4 2" />
          <line x1="92%" y1="34%" x2="72%" y2="84%" stroke="#ef4444" strokeWidth="1.2" opacity="0.5" strokeDasharray="4 2" />
          <line x1="92%" y1="34%" x2="26%" y2="84%" stroke="#ef4444" strokeWidth="1.2" opacity="0.5" strokeDasharray="4 2" />
        </svg>

        {/* Project center */}
        <div className="absolute left-1/2 top-[48%] -translate-x-1/2 -translate-y-1/2 z-10">
          <div className="w-16 h-16 rounded-full bg-[#5AA2AE] flex items-center justify-center shadow-lg shadow-[#5AA2AE]/25">
            <span className="text-white text-[10px] font-bold">Project</span>
          </div>
        </div>

        {/* Segment nodes */}
        {segments.map((s, i) => (
          <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2 z-[5] text-center" style={{ left: `${s.x}%`, top: `${s.y}%` }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-md" style={{ background: s.color, boxShadow: `0 2px 12px ${s.color}30` }}>
              <span className="text-white text-[10px] font-bold">{s.people.length}</span>
            </div>
            <span className="text-[9px] text-slate-500 block mt-0.5">{s.name}</span>
          </div>
        ))}

        {/* People nodes */}
        {segments.map((s, si) => s.people.map((p, pi) => (
          <div key={`p-${si}-${pi}`} className="absolute -translate-x-1/2 -translate-y-1/2 z-[3]" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
            <div className={`w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm ${p.selected ? 'ring-2 ring-red-400 ring-offset-1' : ''}`} style={{ border: `2px solid ${s.color}` }}>
              <UserRound className="w-3 h-3" style={{ color: s.color }} />
            </div>
          </div>
        )))}
      </div>
      <div className="flex items-center justify-center gap-4 mt-2">
        {segments.map((s, i) => (
          <div key={i} className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} /><span className="text-[10px] text-slate-400">{s.name}</span></div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-1 mt-1">
        <div className="w-5 h-0 border-t-[1.5px] border-dashed border-red-400" />
        <span className="text-[9px] text-slate-400">Cross-segment relationships</span>
      </div>
    </div>
  );
}

/* ─── Project Summary ─── */
function ProjectSummarySlide() {
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 className="h-4 w-4 text-[#5AA2AE]" />
        <span className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Project Summary</span>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {[{ v: '314', l: 'Invited', c: '#5AA2AE' }, { v: '310', l: 'Completed', c: '#7FCDC1' }, { v: '98.7%', l: 'Response Rate', c: '#E8A87C' }, { v: '4,274', l: 'Responses', c: '#85C1E9' }].map((s, i) => (
          <div key={i} className="text-center bg-slate-50 rounded-lg p-3">
            <p className="text-lg font-bold" style={{ color: s.c }}>{s.v}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{s.l}</p>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {[{ d: 'Sentiment', s: 6.6, c: '#5AA2AE' }, { d: 'Influence', s: 6.1, c: '#7FCDC1' }, { d: 'Interest', s: 5.4, c: '#E8A87C' }, { d: 'Confidence', s: 4.1, c: '#85C1E9' }].map((d, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="text-xs text-slate-500 w-20">{d.d}</span>
            <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${d.s * 10}%`, background: d.c }} /></div>
            <span className="text-xs font-bold text-slate-700 w-6 text-right">{d.s}</span>
          </div>
        ))}
      </div>
      <div className="text-center pt-2">
        <p className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>6.6<span className="text-sm text-slate-400 font-normal">/10</span></p>
        <p className="text-[10px] text-slate-400">Overall Project Sentiment</p>
      </div>
    </div>
  );
}

/* ─── AI Insights ─── */
function AIInsightsSlide() {
  return (
    <div className="p-6 space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <Brain className="h-4 w-4 text-[#5AA2AE]" />
        <span className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>AI-Powered Insights</span>
        <Sparkles className="h-3 w-3 text-amber-400 ml-auto" />
      </div>
      {[
        { title: 'Sentiment Trend', text: 'Stakeholders in the Engineering segment show a 12% improvement in confidence scores over the last 30 days, driven primarily by improved communication around milestone delivery.', tag: 'Positive', bc: '#dcfce7', tc: '#166534' },
        { title: 'Risk Alert', text: 'Community Members report declining interest (5.4 avg). Three open-ended responses mention concerns about environmental impact assessment timelines.', tag: 'Action Needed', bc: '#fef3c7', tc: '#92400e' },
        { title: 'Key Theme', text: 'Across 4,274 responses, "transparency" and "communication" are the most frequently cited positive factors. "Schedule uncertainty" is the leading concern.', tag: 'Analysis', bc: '#dbeafe', tc: '#1e40af' },
      ].map((insight, i) => (
        <div key={i} className="bg-slate-50 rounded-lg p-3 border-l-2" style={{ borderColor: i === 0 ? '#10b981' : i === 1 ? '#f59e0b' : '#3b82f6' }}>
          <div className="flex items-center gap-2 mb-1">
            <p className="text-xs font-semibold text-slate-700">{insight.title}</p>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full ml-auto font-medium" style={{ background: insight.bc, color: insight.tc }}>{insight.tag}</span>
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed">{insight.text}</p>
        </div>
      ))}
    </div>
  );
}

/* ─── Driver Analysis — 2×2 analysis tiles matching the app UI ─── */
function DriverAnalysisSlide() {
  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Sentiment</h3>
          <span className="text-[10px] text-slate-400">4 questions &bull; 1,098 total responses</span>
        </div>
        <span className="text-[10px] text-slate-400 flex items-center gap-1"><Users className="w-3 h-3" /> 357 stakeholders</span>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {/* Slider tile — Overall Sentiment */}
        <div className="bg-white rounded-lg border border-slate-100 p-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] text-slate-400 flex items-center gap-1"><SlidersHorizontal className="w-3 h-3" /> Slider</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">n=329</span>
          </div>
          <p className="text-xs font-semibold text-slate-800 mb-0.5">Overall Sentiment</p>
          <p className="text-[10px] text-slate-400 mb-2">How's the project going?</p>
          <div className="flex items-baseline gap-1 mb-1.5">
            <span className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>6.5</span>
            <span className="text-[10px] text-slate-400">/10</span>
            <span className="text-[9px] text-slate-300 ml-auto">σ = 2.93</span>
          </div>
          <div className="h-2 rounded-full mb-1.5 relative" style={{ background: 'linear-gradient(to right, #f9a8a8, #fcd77e, #a7e8bd)' }}>
            <div className="absolute top-0 h-full w-0.5 bg-slate-800 rounded" style={{ left: '65%' }} />
          </div>
          <div className="flex justify-between"><span className="text-[8px] text-rose-300">Worst</span><span className="text-[8px] text-emerald-400">Best</span></div>
          {/* Mini histogram */}
          <div className="flex items-end gap-0.5 h-10 mt-2">
            {[{ h: 18, c: '#94a3b8' }, { h: 25, c: '#94a3b8' }, { h: 15, c: '#cbd5e1' }, { h: 55, c: '#94a3b8' }, { h: 85, c: '#6b7280' }].map((b, i) => (
              <div key={i} className="flex-1 rounded-t-sm" style={{ height: `${b.h}%`, background: b.c }} />
            ))}
          </div>
          <div className="flex justify-between mt-0.5">
            {['0-2', '2-4', '4-6', '6-8', '8-10'].map((l, i) => <span key={i} className="text-[7px] text-slate-300">{l}</span>)}
          </div>
        </div>

        {/* Free Text tile — Own Words */}
        <div className="bg-white rounded-lg border border-slate-100 p-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] text-slate-400 flex items-center gap-1"><Type className="w-3 h-3" /> Free Text</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">n=264</span>
          </div>
          <p className="text-xs font-semibold text-slate-800 mb-0.5">Own Words</p>
          <p className="text-[10px] text-slate-400 mb-2">How do you feel about the project?</p>
          <div className="flex items-center gap-1 mb-1.5">
            <span className="text-[9px] text-slate-400">264 responses</span>
            <span className="text-[9px] text-slate-300 ml-auto flex items-center gap-0.5"><Brain className="w-2.5 h-2.5" /> AI Analysis</span>
          </div>
          {/* Sentiment bar */}
          <div className="flex h-2.5 rounded-full overflow-hidden mb-1">
            <div className="bg-emerald-500" style={{ width: '5%' }} />
            <div className="bg-slate-400" style={{ width: '87%' }} />
            <div className="bg-red-400" style={{ width: '8%' }} />
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-[8px] text-emerald-500">5% Positive</span>
            <span className="text-[8px] text-slate-400">87% Neutral</span>
            <span className="text-[8px] text-red-400">9% Concerned</span>
          </div>
          <p className="text-[8px] text-slate-400 mb-1">Key themes identified:</p>
          {[{ t: 'Water Issues', w: 35, p: '8%' }, { t: 'Support/Optimism', w: 22, p: '5%' }, { t: 'Opposition/Concern', w: 18, p: '4%' }].map((th, i) => (
            <div key={i} className="flex items-center gap-1.5 mb-0.5">
              <div className="h-1.5 rounded-full bg-[#5AA2AE]" style={{ width: `${th.w}%` }} />
              <span className="text-[8px] text-slate-500 flex-1 truncate">{th.t}</span>
              <span className="text-[8px] text-slate-400">{th.p}</span>
            </div>
          ))}
        </div>

        {/* Slider tile — Inclusiveness */}
        <div className="bg-white rounded-lg border border-slate-100 p-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] text-slate-400 flex items-center gap-1"><SlidersHorizontal className="w-3 h-3" /> Slider</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">n=264</span>
          </div>
          <p className="text-xs font-semibold text-slate-800 mb-0.5">Inclusiveness</p>
          <p className="text-[10px] text-slate-400 mb-2">Do you feel included as part of the team?</p>
          <div className="flex items-baseline gap-1 mb-1.5">
            <span className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>6.3</span>
            <span className="text-[10px] text-slate-400">/10</span>
            <span className="text-[9px] text-slate-300 ml-auto">σ = 3.01</span>
          </div>
          <div className="h-2 rounded-full mb-1.5 relative" style={{ background: 'linear-gradient(to right, #f9a8a8, #fcd77e, #a7e8bd)' }}>
            <div className="absolute top-0 h-full w-0.5 bg-slate-800 rounded" style={{ left: '63%' }} />
          </div>
          <div className="flex justify-between"><span className="text-[8px] text-rose-300">Not at All</span><span className="text-[8px] text-emerald-400">Absolutely</span></div>
          <div className="flex items-end gap-0.5 h-10 mt-2">
            {[{ h: 22, c: '#94a3b8' }, { h: 30, c: '#94a3b8' }, { h: 12, c: '#cbd5e1' }, { h: 50, c: '#94a3b8' }, { h: 80, c: '#6b7280' }].map((b, i) => (
              <div key={i} className="flex-1 rounded-t-sm" style={{ height: `${b.h}%`, background: b.c }} />
            ))}
          </div>
          <div className="flex justify-between mt-0.5">
            {['0-2', '2-4', '4-6', '6-8', '8-10'].map((l, i) => <span key={i} className="text-[7px] text-slate-300">{l}</span>)}
          </div>
        </div>

        {/* Free Text tile — Unspoken Problem */}
        <div className="bg-white rounded-lg border border-slate-100 p-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] text-slate-400 flex items-center gap-1"><Type className="w-3 h-3" /> Free Text</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">n=241</span>
          </div>
          <p className="text-xs font-semibold text-slate-800 mb-0.5">Unspoken Problem</p>
          <p className="text-[10px] text-slate-400 mb-2">Is there a problem the team isn't discussing?</p>
          <div className="flex items-center gap-1 mb-1.5">
            <span className="text-[9px] text-slate-400">241 responses</span>
            <span className="text-[9px] text-slate-300 ml-auto flex items-center gap-0.5"><Brain className="w-2.5 h-2.5" /> AI Analysis</span>
          </div>
          <div className="flex h-2.5 rounded-full overflow-hidden mb-1">
            <div className="bg-emerald-500" style={{ width: '4%' }} />
            <div className="bg-slate-400" style={{ width: '75%' }} />
            <div className="bg-red-400" style={{ width: '21%' }} />
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-[8px] text-emerald-500">4% Positive</span>
            <span className="text-[8px] text-slate-400">76% Neutral</span>
            <span className="text-[8px] text-red-400">21% Concerned</span>
          </div>
          <p className="text-[8px] text-slate-400 mb-1">Key themes identified:</p>
          {[{ t: 'Leadership Concerns', w: 52, p: '28%' }, { t: 'Politics & Power', w: 35, p: '17%' }, { t: 'Trust Issues', w: 22, p: '11%' }].map((th, i) => (
            <div key={i} className="flex items-center gap-1.5 mb-0.5">
              <div className="h-1.5 rounded-full bg-[#5AA2AE]" style={{ width: `${th.w}%` }} />
              <span className="text-[8px] text-slate-500 flex-1 truncate">{th.t}</span>
              <span className="text-[8px] text-slate-400">{th.p}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Segment Analysis ─── */
function SegmentAnalysisSlide() {
  const segments = ['Contoso', 'Acme', 'Vortex', 'Lunetra'];
  const drivers = ['Sentiment', 'Influence', 'Interest', 'Confidence'];
  const data = [[7.2, 6.5, 5.8, 4.5], [6.8, 6.0, 5.2, 3.8], [6.2, 5.8, 5.6, 4.2], [6.0, 6.3, 5.0, 3.9]];
  const getColor = (v) => {
    if (v >= 7) return '#059669'; if (v >= 6) return '#10b981'; if (v >= 5) return '#fbbf24'; if (v >= 4) return '#f97316'; return '#ef4444';
  };
  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Layers className="h-4 w-4 text-[#5AA2AE]" />
        <span className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Segment Analysis</span>
        <span className="text-[10px] text-slate-400 ml-auto">Organization breakdown</span>
      </div>
      <div className="overflow-hidden rounded-lg border border-slate-100">
        <table className="w-full text-xs">
          <thead><tr className="bg-slate-50">
            <th className="text-left p-2 text-slate-400 font-medium" />
            {drivers.map((d, i) => <th key={i} className="p-2 text-slate-500 font-medium text-center">{d}</th>)}
          </tr></thead>
          <tbody>
            {segments.map((s, si) => (
              <tr key={si} className="border-t border-slate-50">
                <td className="p-2 font-medium text-slate-600">{s}</td>
                {data[si].map((v, di) => (
                  <td key={di} className="p-1.5 text-center">
                    <div className="rounded px-2 py-1.5 text-white font-bold text-[11px]" style={{ background: getColor(v) }}>{v.toFixed(1)}</div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-center gap-1 mt-3">
        {[{ c: '#ef4444', l: '<4' }, { c: '#f97316', l: '4-5' }, { c: '#fbbf24', l: '5-6' }, { c: '#10b981', l: '6-7' }, { c: '#059669', l: '7+' }].map((s, i) => (
          <div key={i} className="flex items-center gap-0.5"><div className="w-3 h-2 rounded-sm" style={{ background: s.c }} /><span className="text-[9px] text-slate-400">{s.l}</span></div>
        ))}
      </div>
    </div>
  );
}

/* ─── Carousel configuration ─── */
const SLIDES = [
  { component: AboutMeSlide, title: 'About Me', icon: User },
  { component: AboutOthersSlide, title: 'About Others', icon: MessageSquare, dark: true },
  { component: PeopleGalaxySlide, title: 'People Galaxy', icon: Users },
  { component: ProjectSummarySlide, title: 'Project Summary', icon: BarChart3 },
  { component: AIInsightsSlide, title: 'AI Insights', icon: Brain },
  { component: DriverAnalysisSlide, title: 'Driver Analysis', icon: TrendingUp },
  { component: SegmentAnalysisSlide, title: 'Segment Analysis', icon: Layers },
];

export default function FeatureCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setActive(i => (i + 1) % SLIDES.length), []);
  const prev = useCallback(() => setActive(i => (i - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [paused, next]);

  const slide = SLIDES[active];
  const SlideComponent = slide.component;

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="feature-carousel"
    >
      <div className="flex flex-wrap justify-center gap-2 mb-6">
        {SLIDES.map((s, i) => {
          const Icon = s.icon;
          return (
            <button key={i} onClick={() => setActive(i)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${
                i === active ? 'bg-[#5AA2AE] text-white shadow-md shadow-[#5AA2AE]/20' : 'bg-white text-slate-500 hover:bg-slate-100 border border-slate-200'
              }`}
              data-testid={`carousel-pill-${i}`}
            >
              <Icon className="h-3 w-3" /><span className="hidden sm:inline">{s.title}</span>
            </button>
          );
        })}
      </div>
      <div className="relative max-w-2xl mx-auto">
        <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-10 w-8 h-8 rounded-full bg-white shadow-md border border-slate-100 flex items-center justify-center hover:bg-slate-50" data-testid="carousel-prev">
          <ChevronLeft className="h-4 w-4 text-slate-400" />
        </button>
        <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-10 w-8 h-8 rounded-full bg-white shadow-md border border-slate-100 flex items-center justify-center hover:bg-slate-50" data-testid="carousel-next">
          <ChevronRight className="h-4 w-4 text-slate-400" />
        </button>
        <div className={`rounded-2xl shadow-xl border border-slate-100 overflow-hidden transition-all duration-500 ${slide.dark ? 'bg-[#1a1f2e]' : 'bg-white'}`}>
          <SlideComponent />
        </div>
        <div className="flex justify-center gap-1.5 mt-4">
          {SLIDES.map((_, i) => (
            <button key={i} onClick={() => setActive(i)} className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? 'w-6 bg-[#5AA2AE]' : 'w-1.5 bg-slate-200 hover:bg-slate-300'}`} />
          ))}
        </div>
      </div>
    </div>
  );
}
